import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { hintHorizontalShift } from './actionHintPlacement'

/** How long the hint stays legible before it fades out. */
const VISIBLE_MS = 8000

/** Space between the bubble's arrow and the control it points at. */
const GAP_PX = 8

/** Smallest gap to leave between the bubble and the side of the screen. */
const EDGE_MARGIN_PX = 8

type AnchorBox = { top: number; left: number; width: number }

/**
 * A line of text pointing down at the control it belongs to, shown for a few
 * seconds and then faded out (BRAI-685).
 *
 * It leaves on a timer rather than waiting to be dismissed. This sits in front
 * of somebody about to start a meeting, and a card they have to close first is
 * one more thing between them and the thing they came to do — the bob on the
 * button says "here", and this says why.
 *
 * Centred over its control, and slid sideways only by as much as a screen edge
 * demands; the arrow stays on the control either way, so a bubble that has
 * been pushed still points at the thing it is about. See
 * `hintHorizontalShift` for the arithmetic and why centred is the default.
 *
 * Rendered into `document.body` and positioned against the control's measured
 * box, rather than placed beside it in the tree. The live-meeting controls sit
 * inside `CollapsibleChrome`, whose `overflow-hidden` is what makes the
 * collapse work at all — anything hanging above the bar is cut off there,
 * which is exactly what happened to the first version of this: only its last
 * line survived. `FeedbackControl` reaches for a portal for the same reason.
 *
 * Stays in the accessibility tree after it fades. The sentence is still true of
 * the button once the pixels are gone, and somebody listening rather than
 * looking never had the few seconds to catch in the first place.
 *
 * @param id Referenced by the control's `aria-describedby`, so the two halves
 *   stay tied together across the portal.
 * @param show Whether the hint is wanted at all. Going false removes it at
 *   once; going true again starts a fresh countdown.
 */
export default function ActionHintBubble({
  id,
  text,
  show,
}: {
  id: string
  text: string
  show: boolean
}) {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)
  const [faded, setFaded] = useState(false)
  const [box, setBox] = useState<AnchorBox | null>(null)
  const [bubbleWidth, setBubbleWidth] = useState(0)

  useEffect(() => {
    if (!show) return undefined
    setFaded(false)
    const timer = window.setTimeout(() => setFaded(true), VISIBLE_MS)
    return () => window.clearTimeout(timer)
  }, [show])

  // Measures the control this belongs to by reading the parent of a marker
  // left behind in the tree, so the component still takes its position from
  // where it was written rather than from a ref the caller has to thread
  // through. Layout effect, so the bubble is never painted at a stale place.
  useLayoutEffect(() => {
    if (!show) {
      setBox(null)
      return undefined
    }
    const host = anchorRef.current?.parentElement
    if (!host) return undefined

    const measure = () => {
      const rect = host.getBoundingClientRect()
      setBox({ top: rect.top, left: rect.left, width: rect.width })
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [show])

  // Second pass, for the bubble's own width: it depends on how the sentence
  // wraps, which is only known once it is in the document. Before paint, so
  // the centred-then-corrected position is never a frame anybody sees.
  useLayoutEffect(() => {
    if (!show) return
    const el = bubbleRef.current
    if (el) setBubbleWidth(el.offsetWidth)
  }, [show, text, box])

  if (!show) return null

  const anchorCentre = box ? box.left + box.width / 2 : 0
  const shift = box
    ? hintHorizontalShift({
        anchorCentre,
        bubbleWidth,
        viewportWidth: window.innerWidth,
        margin: EDGE_MARGIN_PX,
      })
    : 0

  // Anchored by its bottom rather than its top: the height depends on how the
  // sentence wraps, and pinning the edge that touches the control means never
  // having to know it.
  const placement: CSSProperties | null = box && {
    bottom: `${window.innerHeight - box.top + GAP_PX}px`,
    left: `${anchorCentre + shift}px`,
    transform: 'translateX(-50%)',
  }

  return (
    <>
      <span ref={anchorRef} aria-hidden="true" className="hidden" />
      {placement
        ? createPortal(
            <div
              ref={bubbleRef}
              id={id}
              role="tooltip"
              data-testid={id}
              style={placement}
              className={[
                // `pointer-events-none`, because it overlaps whatever is above
                // the control and a reader reaching for that should not hit a
                // hint that is on its way out anyway.
                'pointer-events-none fixed z-50 w-max rounded-lg border px-3 py-2',
                'border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-[var(--shadow-sm)]',
                'text-left text-[11px] font-semibold leading-snug text-[var(--text-primary)]',
                // Capped and allowed to wrap: unbounded, the longer
                // translations ran off the side of a phone.
                'max-w-[min(14rem,calc(100vw-2rem))]',
                'transition-opacity duration-700 motion-reduce:transition-none',
                faded ? 'opacity-0' : 'opacity-100',
              ].join(' ')}
            >
              {text}
              <span
                aria-hidden="true"
                data-testid={`${id}-arrow`}
                // Back off the shift the bubble took, so the arrow lands on the
                // control's middle whether or not an edge moved the box.
                style={{
                  left: `calc(50% - ${shift}px)`,
                  transform: 'translate(-50%, -50%) rotate(45deg)',
                }}
                className={[
                  'absolute top-full h-2 w-2 border-b border-r',
                  'border-[var(--border-color)] bg-[var(--bg-secondary)]',
                ].join(' ')}
              />
            </div>,
            document.body
          )
        : null}
    </>
  )
}
